import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DetailCards } from 'components/detail/view/DetailCards';
import { DetailHeader } from 'components/detail/view/DetailHeader';
import { ScrollVertical } from 'elements/functional/ScrollVertical';
import { EBorders } from 'libs/styles/styling';
import { useContext, useRef } from 'react';
import { IWorldMarker } from 'react-give-me-map';
import { MapRef, Popup } from 'react-map-gl';
import { CtxModal } from 'session/ctxModal';
import styled from 'styled-components/macro';
import { useOnClickOutside } from 'usehooks-ts';

interface Props {
    allMarkers: IWorldMarker[];
    mapRef: React.RefObject<MapRef>;
    autoClose?: boolean;
}

export const DetailModalBubble = ({ allMarkers, mapRef, autoClose }: Props): JSX.Element => {
    const ctxModal = useContext(CtxModal);
    const ref = useRef<HTMLDivElement>(null);
    useOnClickOutside(ref, () => {
        onClose();
    });

    if (!ctxModal.detailModal?.id) {
        return null;
    }

    const onClose = () => {
        ctxModal.setDetailModal(null);
    };

    const marker = allMarkers.find((e) => e.id === ctxModal.detailModal?.markerId);

    const getCoordinates = () => {
        if (!marker) {
            const center = mapRef.current.getCenter();
            return { lat: center.lat, lng: center.lng, offset: 1, anchor: 'center' as const };
        }

        if (marker.elementType === 'v2/icon') {
            return {
                lat: marker.lat,
                lng: marker.lng,
                offset: 200 * marker.elementData.imageSize + 20,
                anchor: 'bottom' as const
            };
        }

        if (
            marker.elementType === 'v2/image' ||
            marker.elementType === 'v2/line' ||
            marker.elementType === 'v2/polygon' ||
            marker.elementType === 'v2/wall' ||
            marker.elementType === 'v2/route'
        ) {
            const coordinates = marker.elementData.coordinates;

            const maxLat = Math.max(...coordinates.map((e) => e.lat));
            const minLng = Math.min(...coordinates.map((e) => e.lng));
            const maxLng = Math.max(...coordinates.map((e) => e.lng));

            return {
                lat: maxLat,
                lng: (minLng + maxLng) / 2,
                offset: 1,
                anchor: 'bottom' as const
            };
        }
    };

    const coordinates = getCoordinates();

    const content =
        ctxModal.detailModal?.id === ctxModal.detailModal?.detail?.storyId ? (
            <S_Wrap ref={ref}>
                <S_DetailHeader header={ctxModal.detailModal.detail.header}>
                    <S_Icon icon={['far', 'xmark']} onClick={onClose} />
                </S_DetailHeader>
                {ctxModal.detailModal.detail.cards?.length > 0 && (
                    <DetailCards cards={ctxModal.detailModal.detail.cards} />
                )}
            </S_Wrap>
        ) : (
            <></>
        );

    return (
        <S_Popup
            $length={ctxModal.detailModal?.detail?.cards?.length ?? 0}
            maxWidth="350px"
            longitude={coordinates.lng}
            latitude={coordinates.lat}
            anchor={coordinates.anchor}
            offset={coordinates.offset}
            closeOnMove={Boolean(autoClose)}
            closeButton={false}
            closeOnClick={Boolean(autoClose)}
            onClose={onClose}
            style={{
                borderRadius: '15px',
                overflow: 'hidden'
            }}
        >
            <S_Content>
                <S_Scroll light={true}>{content}</S_Scroll>
            </S_Content>
        </S_Popup>
    );
};

const S_Wrap = styled.div``;

const S_Popup = styled(Popup)<{ $length: number }>`
    pointer-events: all !important;
    cursor: default;
    .mapboxgl-popup-content {
        padding: 0;
        border-radius: 15px !important;
        box-shadow: 0px 4px 5px rgb(0 0 0/15%) !important;
    }
    .mapboxgl-popup-tip {
        margin: -1px 0 0 0;
        border-top-color: ${(props) => (props.$length > 0 ? '#f1f1f1' : '#ffffff')} !important;
    }
`;

const S_Content = styled.div`
    width: 350px;
    position: relative;
    border-radius: 15px;
    overflow: hidden;
    border: 1px solid ${EBorders.DARK};
`;

const S_Scroll = styled(ScrollVertical)`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    max-height: 300px;
    border-radius: 15px;
`;

const S_DetailHeader = styled(DetailHeader)`
    position: relative;
`;

const S_Icon = styled(FontAwesomeIcon)`
    color: #141c54;
    position: absolute;
    right: 15px;
    top: 15px;
    font-size: 20px;
    cursor: pointer;
    z-index: 1;
`;
